import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Save, Lock, Unlock, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ScoreEntry = () => {
  const { user } = useAuth();
  const canPublish = (user?.roles || []).some((r) => ["super_admin", "director"].includes(r));

  const [teaching, setTeaching] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [term, setTerm] = useState("first");
  const [selectedAsn, setSelectedAsn] = useState(null);
  const [roster, setRoster] = useState([]);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);

  const loadInit = async () => {
    try {
      const [cfg, se] = await Promise.all([
        api.get("/assessments/config"),
        api.get("/academic-sessions"),
      ]);
      setConfig(cfg.data);
      setSessions(se.data);
      const cur = se.data.find((s) => s.is_current) || se.data[0];
      if (cur) setSessionId(cur.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadInit(); }, []);

  const loadTeaching = async () => {
    if (!sessionId) return;
    try {
      const { data } = await api.get("/assessments/my-teaching", { params: { session_id: sessionId, term } });
      setTeaching(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadTeaching(); /* eslint-disable-next-line */ }, [sessionId, term]);

  const pickAsn = async (a) => {
    setSelectedAsn(a); setRoster([]);
    try {
      const { data } = await api.get("/assessments/marks/roster", {
        params: { subject_id: a.subject_id, class_id: a.class_id, arm_id: a.arm_id || undefined, session_id: sessionId, term },
      });
      setRoster(data.rows);
      setLocked(!!data.locked);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRow = (idx, key, val) => {
    setRoster((r) => r.map((row, i) => i === idx ? { ...row, [key]: val === "" ? null : Number(val) } : row));
  };

  const save = async () => {
    if (!selectedAsn) return;
    setBusy(true);
    try {
      const entries = roster.map((r) => ({ student_id: r.student_id, ca1: r.ca1, ca2: r.ca2, exam: r.exam }));
      await api.post("/assessments/marks/bulk", {
        subject_id: selectedAsn.subject_id, class_id: selectedAsn.class_id, arm_id: selectedAsn.arm_id || null,
        session_id: sessionId, term, entries,
      });
      toast.success(`Saved ${entries.length} entries`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!selectedAsn) return;
    if (!window.confirm("Publish results for this CLASS+ARM+TERM? Teachers will not be able to edit until you unlock.")) return;
    try {
      await api.post("/assessments/publish", {
        class_id: selectedAsn.class_id, arm_id: selectedAsn.arm_id || null,
        session_id: sessionId, term,
      });
      toast.success("Results published");
      setLocked(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unlock = async () => {
    if (!selectedAsn) return;
    if (!window.confirm("Unlock this class's results for teacher edits?")) return;
    try {
      await api.post("/assessments/unlock", {
        class_id: selectedAsn.class_id, arm_id: selectedAsn.arm_id || null,
        session_id: sessionId, term,
      });
      toast.success("Results unlocked");
      setLocked(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const w = config?.weights || { ca1: 15, ca2: 15, exam: 60, attendance: 10 };
  const previewTotal = (r) => Math.round(((Number(r.ca1) || 0) + (Number(r.ca2) || 0) + (Number(r.exam) || 0)) * 100) / 100;

  return (
    <div data-testid="score-entry-page">
      <PageHeader
        overline="Assessment"
        title="Score Entry"
        description="Enter CA1, CA2 and Exam scores for the classes and subjects assigned to you. Attendance marks are auto-calculated per student per term."
        actions={
          <>
            <Select value={sessionId} onValueChange={setSessionId}>
              <SelectTrigger className="h-10 w-44 bg-white" data-testid="se-session"><SelectValue placeholder="Session" /></SelectTrigger>
              <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={term} onValueChange={setTerm}>
              <SelectTrigger className="h-10 w-36 bg-white" data-testid="se-term"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First Term</SelectItem>
                <SelectItem value="second">Second Term</SelectItem>
                <SelectItem value="third">Third Term</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      />

      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        {/* left rail: my classes */}
        <Card className="p-3 border-[#E2E8F0] max-h-[calc(100vh-16rem)] overflow-y-auto">
          <div className="text-xs uppercase tracking-wider text-[#9DCD3F] font-medium px-1 pb-2">My Teaching Load</div>
          {teaching.length === 0 ? (
            <p className="text-xs text-[#475569] p-2 italic">No subject assignments for this term.</p>
          ) : (
            <div className="space-y-1">
              {teaching.map((a) => (
                <button key={a.id} onClick={() => pickAsn(a)} className={"w-full text-left rounded-md p-2.5 border transition " + (selectedAsn?.id === a.id ? "bg-[#9DCD3F]/15 border-[#9DCD3F]" : "border-transparent hover:bg-[#FDFBF7]")} data-testid={`asn-pick-${a.id}`}>
                  <div className="text-sm font-medium text-[#1B2C5A]">{a.subject_name}</div>
                  <div className="text-xs text-[#475569] mt-0.5">{a.class_name}{a.arm_name ? ` · ${a.arm_name}` : ""}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* right pane: roster */}
        <div>
          {!selectedAsn ? (
            <EmptyState message="Pick a subject/class from the left to enter scores." testId="se-empty" />
          ) : (
            <Card className="border-[#E2E8F0]">
              <div className="p-4 border-b border-[#E2E8F0] flex flex-wrap items-center gap-3">
                <ClipboardList className="w-5 h-5 text-[#9DCD3F]" />
                <div>
                  <div className="font-display text-lg text-[#1B2C5A]">{selectedAsn.subject_name}</div>
                  <div className="text-xs text-[#475569]">{selectedAsn.class_name}{selectedAsn.arm_name ? ` · ${selectedAsn.arm_name}` : ""} · {term === "first" ? "First" : term === "second" ? "Second" : "Third"} Term</div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {locked ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="lock-badge"><Lock className="w-3 h-3" /> Published (locked)</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="draft-badge"><AlertCircle className="w-3 h-3" /> Draft</span>
                  )}
                  {canPublish && (
                    locked
                      ? <Button size="sm" variant="outline" onClick={unlock} className="border-[#E2E8F0]" data-testid="unlock-btn"><Unlock className="w-3.5 h-3.5 mr-1" /> Unlock</Button>
                      : <Button size="sm" onClick={publish} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="publish-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Publish</Button>
                  )}
                  <Button size="sm" onClick={save} disabled={busy || locked} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="save-marks-btn">
                    {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save All
                  </Button>
                </div>
              </div>
              {roster.length === 0 ? (
                <div className="p-8 text-center text-sm text-[#475569]">No students enrolled in this class/arm for the selected session.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#FDFBF7]">
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Admission #</TableHead>
                        <TableHead className="text-center">CA1 (/{w.ca1})</TableHead>
                        <TableHead className="text-center">CA2 (/{w.ca2})</TableHead>
                        <TableHead className="text-center">Exam (/{w.exam})</TableHead>
                        <TableHead className="text-right">Subtotal (/90)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roster.map((r, i) => (
                        <TableRow key={r.student_id} data-testid={`marks-row-${r.student_id}`}>
                          <TableCell className="text-[#475569]">{i + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {r.photo ? <img src={r.photo} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-[10px]">{(r.full_name?.[0] || "?").toUpperCase()}</span>}
                              <span className="text-sm font-medium text-[#1B2C5A]">{r.full_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{r.admission_no}</TableCell>
                          {["ca1", "ca2", "exam"].map((k) => (
                            <TableCell key={k} className="text-center">
                              <Input type="number" step="0.5" min="0" max={w[k]} value={r[k] ?? ""} disabled={locked}
                                onChange={(e) => updateRow(i, k, e.target.value)}
                                className="h-8 w-20 text-center mx-auto" data-testid={`mark-${k}-${r.student_id}`} />
                            </TableCell>
                          ))}
                          <TableCell className="text-right font-semibold text-[#1B2C5A]">{previewTotal(r)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoreEntry;
